import { designTokens } from "@itandi/itandi-bb-ui";
import { ErrorFeedback } from "@itandi/itandi-bb-ui/ErrorFeedback";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { Label } from "@itandi/itandi-bb-ui/Label";
import type { ChangeEvent, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import Styles from "./ReportImagesArea.module.scss";

/**
 * PDF出力図における撮影場所・備考の最大文字数・行数
 */
const POINT_MAX_CHARS = 25;
const NOTE_MAX_LINES = 8;
const ERROR_MESSAGE = "PDF出力時に文字が見切れるため、入力を制限しています。";
const lineHeight = 50;

type InputType = "point" | "note";

type Props = {
	handleSubmit: (submitValue: string) => void | Promise<void>;
	inputType: InputType;
	value: string;
	viewComponent: ReactNode;
};

const getRows = (node: HTMLTextAreaElement) => {
	const hiddenDiv = document.createElement("div");
	document.body.appendChild(hiddenDiv);

	const {
		borderLeft,
		borderRight,
		boxSizing,
		fontSize,
		paddingLeft,
		paddingRight,
		fontFeatureSettings,
		letterSpacing
	} = getComputedStyle(node);
	const lines = node.value.split(/\r\n|\n/);

	hiddenDiv.style.width = `${String(node.getBoundingClientRect().width)}px`;
	hiddenDiv.style.position = "fixed";
	hiddenDiv.style.left = "0";
	hiddenDiv.style.top = "0";
	hiddenDiv.style.boxSizing = boxSizing;
	hiddenDiv.style.borderLeft = borderLeft;
	hiddenDiv.style.borderRight = borderRight;
	hiddenDiv.style.paddingLeft = paddingLeft;
	hiddenDiv.style.paddingRight = paddingRight;
	hiddenDiv.style.fontSize = fontSize;
	hiddenDiv.style.lineHeight = `${lineHeight}px`;
	hiddenDiv.style.visibility = "hidden";
	hiddenDiv.style.overflowWrap = "anywhere";
	hiddenDiv.style.fontFeatureSettings = fontFeatureSettings;
	hiddenDiv.style.letterSpacing = letterSpacing;

	for (const line of lines) {
		const div = document.createElement("div");
		div.style.whiteSpace = "pre-wrap";
		div.innerText = line || " ";
		hiddenDiv.appendChild(div);
	}

	const rows = Math.round(
		hiddenDiv.getBoundingClientRect().height / lineHeight
	);

	hiddenDiv.remove();

	return rows;
};

export function ReportImageInput({
	handleSubmit,
	inputType,
	value,
	viewComponent
}: Props) {
	const [showInput, setShowInput] = useState(false);
	const [currentValue, setCurrentValue] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [rows, setRows] = useState(0);
	const isNote = inputType === "note";

	const isOverLimit = isNote
		? rows > NOTE_MAX_LINES
		: currentValue.length > POINT_MAX_CHARS;
	const error = isOverLimit ? ERROR_MESSAGE : null;

	useEffect(() => {
		if (showInput) {
			const ref = isNote ? textareaRef.current : inputRef.current;
			ref?.focus();
		}
	}, [showInput, isNote]);

	useEffect(() => {
		if (showInput && isNote && textareaRef.current) {
			const calculatedRows = getRows(textareaRef.current);
			setRows(calculatedRows);
		}
	}, [currentValue, isNote, showInput]);

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const newValue = e.target.value;
			setCurrentValue(newValue);
		},
		[]
	);

	const onBlur = useCallback(async () => {
		if (!error) {
			await handleSubmit(currentValue);
			setShowInput(false);
		}
	}, [error, currentValue, handleSubmit]);

	const handleShowInput = useCallback(() => {
		setShowInput(true);
	}, []);

	if (!showInput) {
		return (
			<button
				className={Styles.ViewButton}
				onClick={handleShowInput}
				onFocus={handleShowInput}
			>
				<span className={isNote ? Styles.ViewButtonContent : undefined}>
					{viewComponent}
				</span>
			</button>
		);
	}

	return (
		<div>
			{isNote ? (
				<Flex direction="column" gap={designTokens["spacing/xxs"]}>
					<InputText
						isInvalid={error !== null}
						maxRows={NOTE_MAX_LINES}
						maxW={"11.8rem"} // 1行12文字に合わせる
						onBlur={onBlur}
						onChange={handleChange}
						ref={textareaRef}
						rows={3}
						value={currentValue}
					/>
					{rows > NOTE_MAX_LINES ? (
						<ErrorFeedback>
							{`PDF出力可能行数\n${rows} / ${NOTE_MAX_LINES} 行`}
						</ErrorFeedback>
					) : (
						<Label color="subtle">
							{`PDF出力可能行数\n${rows} / ${NOTE_MAX_LINES} 行`}
						</Label>
					)}
				</Flex>
			) : (
				<Flex direction="column" gap={designTokens["spacing/xxs"]}>
					<Input
						isInvalid={error !== null}
						onBlur={onBlur}
						onChange={handleChange}
						ref={inputRef}
						value={currentValue}
					/>
					{currentValue.length > POINT_MAX_CHARS ? (
						<ErrorFeedback>{`${currentValue.length} / ${POINT_MAX_CHARS}`}</ErrorFeedback>
					) : (
						<Label color="subtle">{`${currentValue.length} / ${POINT_MAX_CHARS}`}</Label>
					)}
				</Flex>
			)}
			{error ? <ErrorFeedback>{error}</ErrorFeedback> : null}
		</div>
	);
}
